import React, { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MsgAction } from '../../store/actions';
import { Loader } from '../../components';
import { Colors } from '../../config';
import EmptyPane from '../../components/ChatLayout/EmptyPane';
import MessagesChat from './MessagesChat';
import GroupMessages from '../GroupMessages/GroupMessages';

/**
 * Every conversation — direct or group — opens on /messages/:conversationId.
 * Groups used to own a second route (/group-messages/:id) with a duplicate
 * sidebar; that split meant the list rebuilt itself on every switch between the
 * two kinds. One route, one sidebar, and this component picks the pane.
 *
 * The id in the URL is the channel id for a group but the *other participant's*
 * user id for a direct chat, so the conversation list is what distinguishes
 * them. The redux selection is only a fallback for the frame before that list
 * arrives.
 */
const ConversationPane = () => {
  const dispatch = useDispatch();
  const { memberName: conversationId } = useParams();

  const chatList = useSelector((state) => state?.MsgReducer?.chat_list?.data);
  const selected = useSelector((state) => state?.MsgReducer?.selected_chat_user);

  const match = useMemo(() => {
    const group = (chatList?.groups?.rows || []).find(
      (row) => String(row?.id) === String(conversationId)
    );
    if (group) return { isGroup: true, entry: group };

    const chat = (chatList?.chats?.rows || []).find(
      (row) => String(row?.chat_member?.user?.id) === String(conversationId)
    );
    if (chat) return { isGroup: false, entry: chat };

    return null;
  }, [chatList, conversationId]);

  // A selection made elsewhere (creating a group or a chat) lands here before
  // the refreshed list does. Only trust it when it is actually the id in the
  // URL, or a stale selection would decide the pane for the wrong conversation.
  const selectedMatchesUrl =
    !!selected &&
    (String(selected?.id) === String(conversationId) ||
      String(selected?.chat_member?.user?.id) === String(conversationId));

  // The pane resolves the conversation and clears the previous one's stream; the
  // pane it renders owns the fetching, through its own paging hook.
  //
  // This used to fetch the first page here as well. So did the pane's
  // `useInfiniteList` (keyed on the same channel id), which meant every open
  // issued the same cursor-less read TWICE — and a cursor-less read REPLACES
  // `message_chats` in the reducer, so the later of the two responses discarded
  // whatever had been appended since the earlier one landed. A message sent in
  // that window disappeared from the stream while sitting safely on the server.
  const loadedChannelRef = useRef(null);

  useEffect(() => {
    if (!conversationId) return;

    const entry = match?.entry;
    const channelId = entry?.id ?? (selectedMatchesUrl ? selected?.id : null);
    if (!channelId) return;

    if (entry && String(selected?.id) !== String(entry.id)) {
      dispatch(MsgAction.SelectedChatUser(entry));
    }

    // Guarded: the effect also re-runs whenever the conversation list changes,
    // and clearing on every one of those would wipe the open stream.
    if (loadedChannelRef.current === channelId) return;
    loadedChannelRef.current = channelId;

    dispatch(MsgAction.ClearChatMsgs());
  }, [conversationId, match, selected, selectedMatchesUrl, dispatch]);

  // A group row carries no chat_member.user — that is what separates the two
  // shapes while the list is still in flight.
  const isGroup = match ? match.isGroup : selectedMatchesUrl ? !selected?.chat_member?.user : null;

  if (isGroup === null) {
    return chatList ? (
      <EmptyPane />
    ) : (
      <div className="conversation-pane" style={{ display: 'grid', placeItems: 'center' }}>
        <Loader.Circular size={60} color={Colors.redColor} />
      </div>
    );
  }

  return isGroup ? <GroupMessages /> : <MessagesChat />;
};

export default ConversationPane;
